import React, { useState } from "react";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { useUploadDaysEntries } from "../../../hooks/useUploadDaysEntries";
import { Button, SearchBar } from "../../../../common/APICommon";
import { LoadingSpinner } from "../../../../common/Loading";
import { CreateTableNew as CreateTable } from "../../../../common/APICommon";
import { DateInput } from "../../../../common/Input"; // Integrated your custom DateInput
import { SetAuditTrail } from "../../../../components/Reports/AuditTrailReportEmployeeSetup";
import UploadDaysPreviewModal from "./components/UploadDaysPreviewModal";
import { uploadDaysEntriesService } from "../../../services/uploadDaysEntriesService";

const UploadDaysEntriesModule = ({ user, showGeneralMessage }) => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateFilters, setDateFilters] = useState({
    start: new Date().toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const {
    loading,
    data,
    handleSearch,
    setPreviewData,
    previewData,
    fetchRecords,
    uploadBulkData,
    processExcelFile,
  } = useUploadDaysEntries({ showGeneralMessage });

  const onGenerate = () => fetchRecords(dateFilters.start, dateFilters.end);

  const onFinalSubmit = async (payload) => {
    const success = await uploadBulkData(payload);
    if (success) {
      await SetAuditTrail({
        setupArea: "Time Records",
        actionTaken: "insert",
        field: "Excel Upload: Days Worked",
        newValue: `Entries: ${payload.length}`,
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id
      });
      onGenerate();
      setIsUploadModalOpen(false);
    }
  };

  const headers = ["Date", "Emp ID", "Full Name", "Time In", "Time Out", "Hours"];
  const accessors = ["date", "employeeId", "fullName", "timeIn", "timeOut", "hours"];

  const renderCell = (row, accessor) => {
    if (accessor === "date") return new Date(row[accessor]).toLocaleDateString();
    if (accessor.includes("time")) return row[accessor] ? new Date(row[accessor]).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "-";
    if (accessor === "hours") return <span className="font-bold text-blue-700">{parseFloat(row[accessor]).toFixed(2)}</span>;
    return row[accessor] || "-";
  };

  return (
    <div className="relative">
      <LoadingSpinner loading={loading} />

      {/* Standardized Header Section */}
      <div className="w-full py-2 px-4 box-border">
        <div className="text-sm">
          <span className="text-gray-400">{"Time Keeping > "}</span>
          <span className="border-b border-black text-gray-700">{"Upload Days Entries"}</span>
        </div>
        <div className="flex items-center justify-between border-b bg-white">
          <h3 className="text-xl font-bold m-0 text-blue-900 tracking-tight uppercase">
            Upload Days Worked (Fixed Logs)
          </h3>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Filters Card - Matched to LeaveEncashmentModule */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" style={{ border: "1px solid #d7d7d7" }}>
          <div className="border-b border-gray-100 mb-4">
            <span className="text-lg font-semibold text-gray-600">Filters</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <DateInput
              label="Start Date"
              value={dateFilters.start}
              onChange={e => setDateFilters({ ...dateFilters, start: e.target.value })}
            />
            <DateInput
              label="End Date"
              value={dateFilters.end}
              onChange={e => setDateFilters({ ...dateFilters, end: e.target.value })}
            />
          </div>

          <div className="flex gap-2 mt-5 border-t border-gray-200 pt-4">
            <Button
              name="GENERATE VIEW"
              icon={<RestartAltIcon />}
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 border-none shadow cursor-pointer font-bold rounded-lg"
              onClick={onGenerate}
            />
            <Button
              name="UPLOAD EXCEL"
              icon={<FileUploadIcon />}
              className="bg-green-600 hover:bg-green-700 text-white px-5 py-3 border-none shadow cursor-pointer font-bold rounded-lg"
              onClick={() => setIsUploadModalOpen(true)}
            />
            <Button
              name="DOWNLOAD TEMPLATE"
              icon={<FileDownloadIcon />}
              className="bg-gray-400 hover:bg-gray-500 text-white px-5 py-3 border-none shadow cursor-pointer rounded-lg"
              onClick={() => uploadDaysEntriesService.downloadTemplate()}
            />
          </div>
        </div>

        <SearchBar handleFilter={handleSearch} />

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <CreateTable
            tableName="FixedLogsTable"
            headers={headers}
            accessors={accessors}
            data={data}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            itemsPerPage={10}
            totalItems={data.length}
            customCellRenderer={renderCell}
          />
        </div>
      </div>

      <UploadDaysPreviewModal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false);
          setPreviewData([]);
        }}
        onSubmit={onFinalSubmit}
        previewData={previewData}
        onFileSelect={processExcelFile}
        loading={loading}
      />
    </div>
  );
};

export default UploadDaysEntriesModule;